'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Accordion from '@/components/ui/Accordion';
import Footer from '@/components/common/Footer';
import StickyCTA from '@/components/ui/StickyCTA';

export default function FoundationsLander() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const faqItems = [
    {
      id: 'faq-1',
      title: 'What\'s included in AI Business Foundations?',
      content: 'AI automation setup, data analytics infrastructure, custom integrations, and ongoing optimization. We build the systems that power your business operations.'
    },
    {
      id: 'faq-2',
      title: 'How is this different from your marketing services?',
      content: 'Marketing services focus on customer acquisition and retention. Foundations focuses on internal operations: automating workflows, building dashboards, integrating systems, and using AI to improve efficiency.'
    },
    {
      id: 'faq-3',
      title: 'Do I need technical knowledge?',
      content: 'No. We handle all the technical implementation. You tell us what you need, we build it and train your team to use it.'
    },
    {
      id: 'faq-4',
      title: 'Can I combine Foundations with marketing services?',
      content: 'Yes! Many clients use Foundations to automate operations and Execution Coverage or Growth Partner for marketing. We can bundle services for better pricing.'
    }
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <AppImage 
              src="/assets/images/boc_logo_240x90_white-1771394304209.png"
              alt="Best Odds Corp Logo"
              width={180}
              height={68}
              className="cursor-pointer"
            />
          </Link>
          <Link href="/cart">
            <Button variant="primary" size="md" className="border-glow">
              Get Started
            </Button>
          </Link>
        </div>
      </nav>

      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border py-4 px-6">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div>
              <p className="text-h5">AI Business Foundations</p>
              <p className="text-body-sm text-muted-foreground">Automate operations, integrate systems, scale smarter</p>
            </div>
            <Link href="/cart">
              <Button variant="primary" size="lg" className="border-glow">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      )}

      <StickyCTA
        title="AI Business Foundations"
        subtitle="Automate operations, integrate systems, scale smarter"
        ctaText="Get Started"
        ctaHref="/cart"
        showAfterScroll={800}
      />

      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <Badge variant="primary" className="mb-6">AI Business Foundations</Badge>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                Automate operations.
                <br />
                <span className="text-primary">Scale smarter.</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed">
                AI automation, data analytics, and custom integrations. Build the systems that power your business.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Start Building Foundations
                </Button>
              </Link>
              <p className="text-body-sm text-muted-foreground mt-4">
                Custom pricing based on scope. Book a call to discuss.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="reveal p-8 border border-primary bg-surface-1">
                <h3 className="text-h3 mb-6">This is for you if:</h3>
                <ul className="space-y-4 text-body-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're spending hours on manual tasks that could be automated</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You need custom dashboards and reporting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You want to integrate AI into your workflows</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You have systems that don't talk to each other</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're ready to scale operations without hiring more people</span>
                  </li>
                </ul>
              </div>
              <div className="reveal p-8 border border-border bg-surface-1">
                <h3 className="text-h3 mb-6">This is NOT for you if:</h3>
                <ul className="space-y-4 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You're looking for marketing services (try Execution Coverage)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You need a one-time project (this is ongoing optimization)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You're not ready to invest in operational infrastructure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You want off-the-shelf software (we build custom solutions)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                What We Build
              </span>
              <h2 className="text-h2 mb-4">Deliverables</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ AI Automation Workflows</h4>
                <p className="text-body-sm text-muted-foreground">
                  Automate repetitive tasks: data entry, email responses, document processing, lead routing.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Custom Dashboards</h4>
                <p className="text-body-sm text-muted-foreground">
                  Real-time business intelligence dashboards with KPIs, metrics, and predictive analytics.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ System Integrations</h4>
                <p className="text-body-sm text-muted-foreground">
                  Connect your CRM, email, accounting, project management, and marketing tools.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Chatbots & AI Assistants</h4>
                <p className="text-body-sm text-muted-foreground">
                  Custom chatbots for customer support, lead qualification, and internal knowledge bases.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Data Analytics & Reporting</h4>
                <p className="text-body-sm text-muted-foreground">
                  Automated reporting, data visualization, and predictive modeling for better decisions.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Process Documentation</h4>
                <p className="text-body-sm text-muted-foreground">
                  SOPs, training materials, and documentation for every system we build.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Process
              </span>
              <h2 className="text-h2 mb-4">How We Work</h2>
            </div>
            <div className="space-y-8">
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">1</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Discovery & Audit (Week 1)</h4>
                  <p className="text-body-sm text-muted-foreground">
                    We audit your current systems, identify bottlenecks, and map out automation opportunities.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">2</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Strategy & Roadmap (Week 2)</h4>
                  <p className="text-body-sm text-muted-foreground">
                    We create a 90-day implementation roadmap with prioritized projects and ROI estimates.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">3</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Build & Integrate (Weeks 3-8)</h4>
                  <p className="text-body-sm text-muted-foreground">
                    We build automations, integrate systems, and create dashboards. You review and approve at each milestone.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">4</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Train & Optimize (Weeks 9-12)</h4>
                  <p className="text-body-sm text-muted-foreground">
                    We train your team, document processes, and optimize based on real-world usage.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Results
              </span>
              <h2 className="text-h2 mb-4">What Clients Achieve</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "We automated 20 hours of manual data entry per week. ROI paid for itself in 2 months."
                </p>
                <p className="text-body-sm font-semibold">— Chris M., SaaS</p>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "The custom dashboard gives us real-time visibility into every part of the business. Game changer."
                </p>
                <p className="text-body-sm font-semibold">— Nina R., E-commerce</p>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "We scaled from 10 to 50 customers without adding operations headcount. Foundations made it possible."
                </p>
                <p className="text-body-sm font-semibold">— Kevin L., B2B Services</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Questions?
              </span>
              <h2 className="text-h2 mb-4">Common Questions</h2>
            </div>
            <div className="max-w-3xl mx-auto reveal">
              <Accordion items={faqItems} allowMultiple={true} />
            </div>
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Build your foundations.
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Custom pricing based on scope. Book a call to discuss your needs.
              </p>
              <Link href="/book">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Book Discovery Call
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}